import { CommonModule } from '@angular/common';
import {
  Component,
  DestroyRef,
  ElementRef,
  HostListener,
  inject,
  input,
  OnInit,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { LucideDynamicIcon } from '@lucide/angular';
import { catchError, debounceTime, distinctUntilChanged, of, Subject, switchMap } from 'rxjs';
import PERMISSIONS from '../../core/permissions';
import { AppShellService } from '../../core/services/app-shell.service';
import { AuthService } from '../../core/services/auth.service';
import { EventoService } from '../../core/services/evento.service';
import {
  buildNavSearchGroups,
  filterNavByQuery,
  flattenNavItems,
  FlatNavItem,
  mapEventoNavHitsToItems,
  NavSearchResultGroup,
} from '../../core/utils/navigation.util';
import { resolveNavIcon } from '../utils/nav-icon.util';
import { provideNavLucideIcons } from '../utils/nav-lucide-icons';

@Component({
  selector: 'ui-nav-search',
  standalone: true,
  imports: [CommonModule, FormsModule, LucideDynamicIcon],
  providers: [provideNavLucideIcons()],
  template: `
    <div class="ui-nav-search" [class.ui-nav-search--open]="panelOpen()">
      <label class="ui-nav-search__field">
        <svg lucideIcon="search" [size]="18" class="ui-nav-search__icon" aria-hidden="true"></svg>
        <input
          type="search"
          class="ui-nav-search__input"
          [placeholder]="placeholder()"
          [(ngModel)]="query"
          (focus)="onFocus()"
          (input)="onQueryChange()"
          (keydown)="onKeydown($event)"
          autocomplete="off"
          aria-label="Buscar páginas e eventos"
          [attr.aria-expanded]="panelOpen()"
          aria-controls="ui-nav-search-results"
        />
        @if (query) {
          <button type="button" class="ui-nav-search__clear" aria-label="Limpar busca" (click)="clear()">
            <svg lucideIcon="x" [size]="16"></svg>
          </button>
        }
      </label>

      @if (panelOpen()) {
        <div id="ui-nav-search-results" class="ui-nav-search__panel" role="listbox">
          @if (loading()) {
            <p class="ui-nav-search__empty">Buscando...</p>
          } @else if (resultGroups().length === 0) {
            <p class="ui-nav-search__empty">Nenhum resultado para "{{ query }}"</p>
          } @else {
            @for (group of resultGroups(); track group.id) {
              <section class="ui-nav-search__group" role="group" [attr.aria-label]="group.label">
                <header class="ui-nav-search__group-header">
                  <svg [lucideIcon]="group.icon" [size]="14" aria-hidden="true"></svg>
                  <span class="ui-nav-search__group-label">{{ group.label }}</span>
                  <span class="ui-nav-search__group-count">{{ group.items.length }}</span>
                </header>
                <ul class="ui-nav-search__group-list">
                  @for (item of group.items; track trackItem(item)) {
                    <li>
                      <button type="button" class="ui-nav-search__result" role="option" (click)="go(item)">
                        <svg
                          [lucideIcon]="resolveNavIcon(item.icon, group.id === 'events' ? 'calendar' : 'search')"
                          [size]="16"
                          class="ui-nav-search__result-icon"
                          aria-hidden="true"
                        ></svg>
                        <span class="ui-nav-search__result-text">
                          <span class="ui-nav-search__result-row">
                            <span class="ui-nav-search__result-label">{{ item.label }}</span>
                            @if (item.badge) {
                              <span class="ui-nav-search__result-badge">{{ item.badge }}</span>
                            }
                          </span>
                          @if (item.detail) {
                            <span class="ui-nav-search__result-detail">{{ item.detail }}</span>
                          } @else if (item.path) {
                            <span class="ui-nav-search__result-detail">{{ item.path }}</span>
                          }
                        </span>
                      </button>
                    </li>
                  }
                </ul>
              </section>
            }
          }
        </div>
      }
    </div>
  `,
  styles: [`
    :host { display: block; width: 100%; min-width: 0; }
    :host {
      display: flex;
      align-items: center;
      width: 100%;
      min-width: 0;
    }

    .ui-nav-search { position: relative; width: 100%; min-width: 0; }
    .ui-nav-search__field {
      display: flex; align-items: center; gap: 0.5rem;
      padding: 0.4375rem 0.75rem; border-radius: 0.5rem;
      border: 1px solid var(--border-color, #e8e8e8);
      background: var(--surface-muted, #fafafa);
      transition: border-color 0.15s, box-shadow 0.15s, background 0.15s;
      cursor: text;
    }
    .ui-nav-search--open .ui-nav-search__field,
    .ui-nav-search__field:focus-within {
      background: var(--surface-bg, #fff);
      border-color: color-mix(in srgb, var(--brand-primary, #1677ff) 45%, var(--border-color, #e8e8e8));
      box-shadow: 0 0 0 3px color-mix(in srgb, var(--brand-primary, #1677ff) 12%, transparent);
    }
    .ui-nav-search__icon { color: var(--text-muted, #8c8c8c); flex-shrink: 0; }
    .ui-nav-search__input {
      flex: 1; min-width: 0; border: none; background: transparent;
      font-size: 0.875rem; color: var(--text-primary, #1f1f1f); outline: none;
    }
    .ui-nav-search__input::placeholder { color: var(--text-muted, #8c8c8c); }
    .ui-nav-search__clear {
      display: inline-flex; padding: 0.125rem; border: none; background: none;
      color: var(--text-muted, #8c8c8c); cursor: pointer; border-radius: 0.25rem;
    }
    .ui-nav-search__clear:hover {
      color: var(--text-primary, #1f1f1f);
      background: var(--surface-muted, #f5f5f5);
    }
    .ui-nav-search__panel {
      position: absolute; top: calc(100% + 0.375rem); left: 0; right: 0; z-index: 50;
      padding: 0.375rem; border-radius: 0.625rem;
      border: 1px solid var(--border-color, #e8e8e8);
      background: var(--surface-bg, #fff);
      box-shadow: 0 12px 32px rgba(0, 0, 0, 0.12);
      max-height: 22rem; overflow-y: auto;
    }
    .ui-nav-search__empty {
      margin: 0; padding: 0.75rem 0.875rem;
      font-size: 0.8125rem; color: var(--text-muted, #8c8c8c);
    }
    .ui-nav-search__group + .ui-nav-search__group {
      margin-top: 0.375rem;
      padding-top: 0.375rem;
      border-top: 1px solid var(--border-color, #f0f0f0);
    }
    .ui-nav-search__group-header {
      display: flex; align-items: center; gap: 0.375rem;
      padding: 0.375rem 0.625rem 0.25rem;
      color: var(--text-muted, #8c8c8c);
      font-size: 0.6875rem; font-weight: 600;
      letter-spacing: 0.04em; text-transform: uppercase;
    }
    .ui-nav-search__group-label { flex: 1; min-width: 0; }
    .ui-nav-search__group-count {
      display: inline-flex; align-items: center; justify-content: center;
      min-width: 1.125rem; height: 1.125rem; padding: 0 0.3125rem;
      border-radius: 999px; background: var(--surface-muted, #f5f5f5);
      font-size: 0.625rem; font-weight: 700; letter-spacing: 0;
    }
    .ui-nav-search__group-list {
      list-style: none; margin: 0; padding: 0;
    }
    .ui-nav-search__result {
      display: flex; align-items: flex-start; gap: 0.625rem; width: 100%;
      padding: 0.625rem 0.75rem; border: none; border-radius: 0.375rem;
      background: none; text-align: left; cursor: pointer;
      color: var(--text-primary, #1f1f1f); font-size: 0.875rem;
    }
    .ui-nav-search__result-icon { flex-shrink: 0; margin-top: 0.125rem; color: var(--text-muted, #8c8c8c); }
    .ui-nav-search__result:hover { background: var(--surface-muted, #f5f5f5); }
    .ui-nav-search__result-text { display: flex; flex-direction: column; gap: 0.125rem; min-width: 0; flex: 1; }
    .ui-nav-search__result-row {
      display: flex; align-items: center; gap: 0.5rem; min-width: 0;
    }
    .ui-nav-search__result-label {
      font-weight: 500; min-width: 0;
      overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
    }
    .ui-nav-search__result-badge {
      flex-shrink: 0; padding: 0.0625rem 0.4375rem;
      border-radius: 999px; font-size: 0.6875rem; font-weight: 600;
      background: color-mix(in srgb, var(--brand-primary, #1677ff) 12%, transparent);
      color: var(--brand-primary, #1677ff);
    }
    .ui-nav-search__result-detail {
      font-size: 0.75rem; color: var(--text-muted, #8c8c8c);
      overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
    }
    :host-context(.ui-topbar--primary) .ui-nav-search__field {
      background: var(--topbar-surface, var(--on-brand-primary-subtle));
      border-color: var(--topbar-border, var(--on-brand-primary-border));
    }
    :host-context(.ui-topbar--primary) .ui-nav-search--open .ui-nav-search__field,
    :host-context(.ui-topbar--primary) .ui-nav-search__field:focus-within {
      background: var(--surface-bg, #fff);
      border-color: color-mix(in srgb, var(--brand-primary, #1677ff) 45%, var(--border-color, #e8e8e8));
      box-shadow: 0 0 0 3px color-mix(in srgb, var(--brand-primary, #1677ff) 12%, transparent);
    }
    :host-context(.ui-topbar--primary) .ui-nav-search__icon,
    :host-context(.ui-topbar--primary) .ui-nav-search__input {
      color: var(--topbar-fg, var(--on-brand-primary));
    }
    :host-context(.ui-topbar--primary) .ui-nav-search__input::placeholder {
      color: var(--topbar-fg-muted, var(--on-brand-primary-muted));
    }
    :host-context(.ui-topbar--primary) .ui-nav-search--open .ui-nav-search__icon,
    :host-context(.ui-topbar--primary) .ui-nav-search--open .ui-nav-search__input,
    :host-context(.ui-topbar--primary) .ui-nav-search__field:focus-within .ui-nav-search__icon,
    :host-context(.ui-topbar--primary) .ui-nav-search__field:focus-within .ui-nav-search__input {
      color: var(--text-primary, #1f1f1f);
    }
    :host-context(.ui-topbar--primary) .ui-nav-search--open .ui-nav-search__input::placeholder,
    :host-context(.ui-topbar--primary) .ui-nav-search__field:focus-within .ui-nav-search__input::placeholder {
      color: var(--text-muted, #8c8c8c);
    }
    :host-context(.ui-topbar--primary) .ui-nav-search__clear {
      color: var(--topbar-fg-muted, var(--on-brand-primary-muted));
    }
    :host-context(.ui-topbar--primary) .ui-nav-search__clear:hover {
      color: var(--topbar-fg, var(--on-brand-primary));
      background: var(--topbar-surface-hover, var(--on-brand-primary-hover));
    }
  `],
})
export class UiNavSearchComponent implements OnInit {
  placeholder = input('Buscar páginas, menus e eventos...');

  private shell = inject(AppShellService);
  private router = inject(Router);
  private host = inject(ElementRef<HTMLElement>);
  private eventos = inject(EventoService);
  private auth = inject(AuthService);
  private destroyRef = inject(DestroyRef);

  readonly resolveNavIcon = resolveNavIcon;

  query = '';
  readonly resultGroups = signal<NavSearchResultGroup[]>([]);
  readonly panelOpen = signal(false);
  readonly loading = signal(false);

  private allItems: FlatNavItem[] = [];
  private readonly query$ = new Subject<string>();
  private canSearchEventos = false;

  ngOnInit(): void {
    this.allItems = flattenNavItems(this.shell.routes, (item) => this.shell.canShow(item));
    this.canSearchEventos =
      this.auth.hasPermission('view_eventos') || this.auth.hasPermission(PERMISSIONS.MANAGE_EVENTOS);

    this.query$
      .pipe(
        debounceTime(300),
        distinctUntilChanged(),
        switchMap((term) => {
          const trimmed = term.trim();
          if (!trimmed || trimmed.length < 2) {
            this.loading.set(false);
            return of({ pages: [] as FlatNavItem[], events: [] as FlatNavItem[] });
          }

          const pages = filterNavByQuery(this.allItems, trimmed, 8);
          if (!this.canSearchEventos) {
            this.loading.set(false);
            return of({ pages, events: [] as FlatNavItem[] });
          }

          this.loading.set(true);
          return this.eventos.searchNav(trimmed, 8).pipe(
            switchMap((resp) => {
              const hits = resp?.data ?? [];
              return of({ pages, events: mapEventoNavHitsToItems(hits) });
            }),
            catchError(() => of({ pages, events: [] as FlatNavItem[] })),
          );
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(({ pages, events }) => {
        this.loading.set(false);
        this.resultGroups.set(buildNavSearchGroups(pages, events));
      });
  }

  onFocus(): void {
    if (this.query.trim()) this.panelOpen.set(true);
  }

  onQueryChange(): void {
    const trimmed = this.query.trim();
    this.panelOpen.set(!!trimmed);
    if (!trimmed) {
      this.resultGroups.set([]);
      this.loading.set(false);
      return;
    }
    if (trimmed.length < 2) {
      this.resultGroups.set(buildNavSearchGroups(filterNavByQuery(this.allItems, trimmed, 8), []));
      this.loading.set(false);
      return;
    }
    this.query$.next(trimmed);
  }

  onKeydown(event: KeyboardEvent): void {
    if (event.key === 'Escape') this.close();
  }

  clear(): void {
    this.query = '';
    this.resultGroups.set([]);
    this.panelOpen.set(false);
    this.loading.set(false);
  }

  go(item: FlatNavItem): void {
    this.router.navigateByUrl(item.link);
    this.close();
  }

  close(): void {
    this.panelOpen.set(false);
  }

  trackItem(item: FlatNavItem): string {
    return `${item.kind ?? 'page'}:${item.link}`;
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    if (!this.host.nativeElement.contains(event.target as Node)) {
      this.panelOpen.set(false);
    }
  }
}
